import { convertTimeZone, has } from '@/commonFunctions/utils'
import { ButtonPrimary, ShareButton } from '@/components/button'
import { EventsIconRounded } from '@/components/icons'
import { NotLoginModal } from '@/components/modal'
import { EventDetailHeaderLoading } from '@/components/skeleton'

import { EVENT_MODIFY } from '@/constants/Common'
import { ROUTE_CLUBS, ROUTE_DASHBOARD, ROUTE_EVENTS } from '@/constants/Routes'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { setVerifyEmailModalState } from '@/pages/user/userSlice'
import { useEndEvent } from '@/services'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { getEventDetail } from '../eventSlice'
import { eventSelector } from '../selectors'
import { EndEventModal } from './EndEventModal'

export const DetailHeader = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { eventId } = useParams()
  const dispatch = useAppDispatch()
  const { isLoading, event } = useAppSelector(eventSelector)
  const { user } = useAppSelector(userSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { token } = useAppSelector(authSelector)

  const { mutate: endEvent } = useEndEvent()

  const [confirmModal, setConfirmModal] = useState(false)
  const [notifyModal, setNotifyModal] = useState(false)

  const eventStart =
    event?.duration?.[0] && !event?.startDate
      ? convertTimeZone(event.duration[0])
      : event?.startDate && new Date(event?.startDate)

  const eventEnd =
    event?.duration?.[1] && !event?.endDate
      ? convertTimeZone(event.duration[1])
      : event?.endDate && new Date(event?.endDate)

  const eventStartString = eventStart ? `${moment(eventStart).format('MMM DD')}` : 'Unknown Start Date'

  const eventEndString = eventEnd ? `${moment(eventEnd).format('MMM DD, YY')}` : 'Unknown End Date'

  const eventString = `${moment(event?.duration?.[0])?.format('MMMM DD')} - ${moment(event?.duration?.[1])?.format(
    'MMMM DD, YYYY'
  )}`

  const shareUrl = window.location.href
  const shareText = `Hi, have a look at this event \nEvent Name: ${event?.leagueName} \nEvent Dates: ${eventString} 
   \nPlease click the below link to view this event`

  const editLeague = () => {
    navigate(`${ROUTE_EVENTS}/${eventId}/edit`)
  }

  const onCheckVerifyEmail = () => {
    if (!user?.isValidEmail) dispatch(setVerifyEmailModalState(true))
  }

  const onCloseConfirmModal = () => setConfirmModal(false)

  const handleEndEvent = () => {
    if (eventId) {
      endEvent(eventId, {
        onSuccess: () => {
          dispatch(getEventDetail(eventId))
          onCloseConfirmModal()
        },
      })
    }
  }

  return isLoading ? (
    <EventDetailHeaderLoading />
  ) : (
    <>
      <div className="flex">
        {event?.mediaUrl ? (
          <img src={event?.mediaUrl} className="w-10 h-10 rounded-full bg-white" alt="" />
        ) : (
          <EventsIconRounded className="text-slate-300 w-10 h-10" />
        )}
        <div className="w-full flex flex-col gap-y-2 ml-3">
          {event?.duration || (event?.startDate && event?.endDate) ? (
            <span className="text-white text-xs font-medium text-opacity-60 uppercase">
              {`${eventStartString} - ${eventEndString}`}
            </span>
          ) : null}
          <p className="text-white text-xl md:text-[32px] md:leading-10 font-bold max-w-[240px] xs:max-w-[280px] sm:max-w-xl md:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl text-ellipsis whitespace-nowrap overflow-hidden">
            {event?.leagueName}
          </p>
          <div>
            {token ? (
              <Link
                to={user?.isValidEmail ? `${ROUTE_CLUBS}/${event?.clubId}` : ROUTE_DASHBOARD}
                className="text-white font-semibold text-md underline underline-offset-2"
                onClick={onCheckVerifyEmail}
              >
                {event?.clubName}
              </Link>
            ) : (
              <p className="text-white font-semibold text-md underline underline-offset-2">{event?.clubName}</p>
            )}
          </div>
          <p className="text-white text-opacity-80 text-sm">{event?.address?.formattedAddress}</p>
          <div className="flex py-1 md:py-2 gap-x-2">
            {event?.registrationUrl ? (
              <ButtonPrimary
                title={t('event.register')}
                className="hidden lg:flex event-register-btn"
                onClick={() =>
                  window.open(
                    event?.registrationUrl?.startsWith('http')
                      ? event?.registrationUrl
                      : `https://${event?.registrationUrl}`
                  )
                }
              >
                {event?.registrationUrl ? (
                  <Tooltip anchorSelect=".event-register-btn" place="bottom">
                    <p className="max-w-sm">{t('club.leaveDUPRWarning')}</p>
                  </Tooltip>
                ) : (
                  <></>
                )}
              </ButtonPrimary>
            ) : null}
            {has(permissions, EVENT_MODIFY) ? (
              <>
                <>
                  <button
                    className="hidden lg:flex items-center justify-center py-2 px-4 rounded-full gap-x-1 text-sm font-semibold bg-opacity-10 hover:bg-opacity-30 bg-white text-white"
                    onClick={editLeague}
                  >
                    {t('event.editEvent')}
                  </button>
                </>
              </>
            ) : (
              ''
            )}
            <ShareButton
              shareUrl={shareUrl}
              shareText={shareText}
              pageName={TRACKING_TYPES.EVENT_HOME}
              shareContent={TRACKING_TYPES.EVENT_SHARE}
            />
          </div>
        </div>
      </div>
      <EndEventModal open={confirmModal} onClose={onCloseConfirmModal} onSubmit={handleEndEvent} />
      <NotLoginModal open={notifyModal} onClose={() => setNotifyModal(false)} title={t('common.signUpLogin')} />
    </>
  )
}
